import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Breadcrumb, BreadcrumbLinkMouseEvent } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { folderIcon } from "@progress/kendo-svg-icons";
import { useNotifications } from "@presentation/components/notifications";
import { useFastaFilesLocationName, useRawFilesLocationName, useResultsLocationName } from "@presentation/hooks/useStorageLocations";
import type { PathTypeSchema } from "@domain/schemas/BI";

// The folder-listing endpoint uses Libraries to select the Results storage container; Results is
// intentionally not a PathType because resultsPath is a scalar configuration value, not a facet.
export const RESULTS_PATH_BROWSE_PATH_TYPE = "Libraries" as PathTypeSchema;

interface FolderPickerDialogProps {
  pathType: PathTypeSchema;
  /** Where browsing starts - typically the path `Input`'s current value, so editing an existing
  path opens already positioned near it rather than always restarting at the facet's root. */
  initialFolder: string;
  /** Caller-supplied so this dialog works for every level (deployment/team/project each expose
  their own `listFoldersAsync`, backed by the same level-agnostic endpoint). */
  listFoldersAsync: (pathType: PathTypeSchema, folder: string) => Promise<string[]>;
  onSelect: (folder: string) => void;
  onClose: () => void;
}

// Kendo's BreadcrumbLink only dispatches `onItemSelect` when the clicked item's `id` is truthy, so
// Root can't use an empty-string id (never fires) - a sentinel that can't collide with a real folder path.
const ROOT_BREADCRUMB_ID = "<root>";

const buildBreadcrumbs = (folder: string, locationName: string): { id: string; text: string }[] => {
  const items = [{ id: ROOT_BREADCRUMB_ID, text: `Location: ${locationName}` }];
  if (!folder) return items;

  let accumulated = "";
  for (const segment of folder.split("/")) {
    accumulated = accumulated ? `${accumulated}/${segment}` : segment;
    items.push({ id: accumulated, text: segment });
  }
  return items;
};

/**
Browses one level at a time within a `PathType` facet's own container (see `IStorageFolderRepository`) -
the "Browse" affordance next to a storage path `Input`. Never the only way to set a path: the caller's
`Input` stays freely editable regardless of what gets picked here, since a path need not already exist
to be configured (e.g. reserving a location before anyone has uploaded to it yet).
*/
export const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({ pathType, initialFolder, listFoldersAsync, onSelect, onClose }) => {
  const { pushNotification } = useNotifications();
  const rawLocationName = useRawFilesLocationName();
  const fastaLocationName = useFastaFilesLocationName();
  const resultsLocationName = useResultsLocationName();
  let locationName = fastaLocationName;
  if (pathType === "Raw") {
    locationName = rawLocationName;
  } else if (pathType === "Libraries") {
    locationName = resultsLocationName;
  }
  const [currentFolder, setCurrentFolder] = useState(initialFolder);
  const [subfolders, setSubfolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadFolders = async () => {
      setLoading(true);
      try {
        const folders = await listFoldersAsync(pathType, currentFolder);
        if (!isCancelled) setSubfolders(folders);
      } catch {
        if (!isCancelled) {
          pushNotification("Could not list folders here.", "error", 10_000);
          setSubfolders([]);
        }
      }
      if (!isCancelled) setLoading(false);
    };

    void loadFolders();
    return () => {
      isCancelled = true;
    };
  }, [pathType, currentFolder, listFoldersAsync, pushNotification]);

  const handleBreadcrumbClick = (event: BreadcrumbLinkMouseEvent) => {
    const id = String(event.id ?? "");
    setCurrentFolder(id === ROOT_BREADCRUMB_ID ? "" : id);
  };

  return (
    <Dialog title="Choose a folder" onClose={onClose} width={480} height={480}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
        <Breadcrumb data={buildBreadcrumbs(currentFolder, locationName)} onItemSelect={handleBreadcrumbClick} size="medium" />
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
          {loading ? (
            <p style={{ padding: 12, color: "#64748b" }}>Loading…</p>
          ) : subfolders.length === 0 ? ( // eslint-disable-line unicorn/no-nested-ternary -- three mutually exclusive render states (loading/empty/list)
            <p style={{ padding: 12, color: "#64748b" }}>No subfolders here.</p>
          ) : (
            subfolders.map((folder) => (
              <div
                key={folder}
                role="button"
                tabIndex={0}
                onClick={() => setCurrentFolder(folder)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") setCurrentFolder(folder);
                }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
              >
                <SvgIcon icon={folderIcon} />
                {folder.split("/").at(-1)}
              </div>
            ))
          )}
        </div>
      </div>
      <DialogActionsBar>
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor="primary" onClick={() => onSelect(currentFolder)}>
          Use this folder
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
